import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanMatchFn, Params, Router, UrlTree } from '@angular/router';
import { combineLatest, map, Observable, of, switchMap } from 'rxjs';

import { LDFeatureKey } from '@@core/services/launch-darkly.service';
import { PermissionCode } from '@@settings/model/permissions.model';
import { PermissionsService } from '@@settings/services/permissions.service';

import { FeatureService } from '../services/features.service';

/**
 * Route guard that controls access based on LaunchDarkly feature flags and optional permission checks.
 *
 * Logic:
 * 1. If at least one of the feature flags in `route.data.features` is enabled, the route is allowed to activate.
 * 2. If a `route.data.enforcePermissionBasedOnFeatureFlag` is defined:
 *    - The guard checks if that specific feature flag is enabled.
 *    - If it's ON, the user must also have the permission defined in `route.data.permission`.
 *    - If it's OFF, permission is not enforced.
 * 3. If no features are active or the permission check fails (when enforced), the user is redirected.
 *
 * Supports optional redirection via `route.data.redirectTo` and `route.data.keepQueryParams`.
 *
 */

export const ldFeatureMatchGuard: CanMatchFn = (route: ActivatedRouteSnapshot) => {
	const featureService = inject(FeatureService);
	const router = inject(Router);
	const permissionsService = inject(PermissionsService);

	const features = (route.data?.features as LDFeatureKey[]) || [];
	const permission: PermissionCode = route.data?.permission;
	const enforcePermissionBasedOnFeatureFlag: LDFeatureKey = route.data?.enforcePermissionBasedOnFeatureFlag;

	return checkRouteEnabled(features, featureService).pipe(
		switchMap(routeEnabled => {
			if (!routeEnabled) {
				return of(buildRedirectTree(router, route));
			}

			if (!enforcePermissionBasedOnFeatureFlag) {
				return of(true);
			}

			const isPermittedByFlag = checkPermissionByFlag(
				featureService,
				permissionsService,
				router,
				route,
				enforcePermissionBasedOnFeatureFlag,
				permission,
			);
			return isPermittedByFlag;
		}),
	);
};

const checkRouteEnabled = (features: LDFeatureKey[], featureService: FeatureService): Observable<boolean> =>
	isAtLeastOneFlagActive(features, featureService);

const isAtLeastOneFlagActive = (features: LDFeatureKey[], featureService: FeatureService): Observable<boolean> => {
	if (!features || features.length === 0) {
		// Explanation: if no features are defined, the route should be enabled.
		// Mathematically: The empty set (∅) is a subset of every set, including itself.
		// Further checks are done in the guard.
		return of(true);
	}

	const observables = features.map(feature => featureService.isFeatureOn(feature));

	return combineLatest(observables).pipe(map(results => results.some(result => result)));
};

const replaceUrlSegments = (router: Router, redirectTo: string, params: Params, queryParams: Params): UrlTree => {
	const segments = redirectTo.split('/').map(segment => {
		if (segment.startsWith(':')) {
			const paramKey = segment.substring(1);
			return params[paramKey] as string[];
		}
		return segment;
	});
	return router.createUrlTree(segments, { queryParams });
};

const buildRedirectTree = (router: Router, route: ActivatedRouteSnapshot): UrlTree => {
	const params = route.params || {};
	const queryParams = route.queryParams || {};
	const redirectTo = (route.data?.redirectTo as string) || '/';

	return route.data?.keepQueryParams
		? replaceUrlSegments(router, redirectTo, params, queryParams)
		: router.createUrlTree([redirectTo]);
};

const checkPermissionByFlag = (
	featureService: FeatureService,
	permissionsService: PermissionsService,
	router: Router,
	route: ActivatedRouteSnapshot,
	featureFlag: LDFeatureKey,
	permission: PermissionCode,
): Observable<boolean | UrlTree> =>
	featureService.isFeatureOn(featureFlag).pipe(
		map(isPermissionFFEnabled => {
			if (!isPermissionFFEnabled) {
				return true;
			}

			const hasPermission = permissionsService.userHasPermission(permission);
			return hasPermission ? true : buildRedirectTree(router, route);
		}),
	);
